import _ from 'lodash';

import { checkFormState, normalizeOptionValues } from '@/utils/core/business-logic';
import { getEnumValue } from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { getEnumNamespace } from '@/utils/infrastructure';

/** Enum keys excluded from onboarding (not registrable). */
const ONBOARDING_EXCLUDED_ENUM_KEYS = new Set(['None']);

/** Actors that create a row in channel entity tables */
const ENTITY_CHANNEL_ACTOR_ENUM_KEYS = Object.freeze([
  'Distributor',
  'Partner',
  'Reseller',
  'ReferralAgent',
]);

const toActorKindApiValue = (enumKey) => _.kebabCase(enumKey);

const buildActorKindsFromEnum = (enumObject) =>
  Object.freeze(
    Object.fromEntries(
      Object.keys(enumObject)
        .filter((key) => !ONBOARDING_EXCLUDED_ENUM_KEYS.has(key))
        .map((key) => [_.snakeCase(key).toUpperCase(), toActorKindApiValue(key)]),
    ),
  );

/** API string values for onboarding, derived from CoreEnums.ChannelActorKind. */
export const ACTOR_KINDS = buildActorKindsFromEnum(CoreEnums.ChannelActorKind);

const VALID_ACTOR_KINDS = new Set(Object.values(ACTOR_KINDS));

const ENTITY_ACTOR_KIND_API_VALUES = new Set(
  ENTITY_CHANNEL_ACTOR_ENUM_KEYS.map(toActorKindApiValue),
);

/** Maps `?type=` query param to canonical kebab-case `actorKind`. */
export const resolveActorKindFromQuery = (typeParam) => {
  if (!typeParam) return undefined;
  if (VALID_ACTOR_KINDS.has(typeParam)) return typeParam;
  const normalized = _.kebabCase(typeParam);
  return VALID_ACTOR_KINDS.has(normalized) ? normalized : undefined;
};

export const isEntityActorKind = (actorKind) => ENTITY_ACTOR_KIND_API_VALUES.has(actorKind);

/** Backend ChannelOnboardingStep enum values (kebab-case). */
export const ONBOARDING_STEP_API_VALUES = Object.freeze({
  'registration-type': 'ENTITY_TYPE',
  'automy-plan': 'PLAN',
  company: 'COMPANY',
  'channel-actor': 'ACTOR',
  'commercial-agreement': 'AGREEMENT',
  billing: 'BILLING',
  summary: 'SUMMARY',
});

export const WIZARD_STEPS = Object.freeze({
  ENTITY_TYPE: 0,
  PLAN: 1,
  COMPANY: 2,
  ACTOR: 3,
  AGREEMENT: 4,
  BILLING: 5,
  SUMMARY: 6,
});

const DEFAULT_ENTITY_REQUIRED_STEPS = Object.freeze([
  'registration-type',
  'automy-plan',
  'company',
  'channel-actor',
  'commercial-agreement',
  'billing',
  'summary',
]);

const DEFAULT_NON_ENTITY_REQUIRED_STEPS = Object.freeze([
  'registration-type',
  'automy-plan',
  'company',
  'billing',
  'summary',
]);

export const getDefaultRequiredStepsForActorKind = (actorKind) =>
  isEntityActorKind(actorKind) ? DEFAULT_ENTITY_REQUIRED_STEPS : DEFAULT_NON_ENTITY_REQUIRED_STEPS;

const mapRequiredStepsToWizardSteps = (requiredSteps = []) => {
  const mapped = requiredSteps
    .map((step) => {
      const stepKey = ONBOARDING_STEP_API_VALUES[step];
      return stepKey ? WIZARD_STEPS[stepKey] : null;
    })
    .filter((step) => step !== null);

  return mapped.length ? mapped : null;
};

export const getWizardStepsForActorKind = (actorKind, registrationOptions = []) => {
  if (!actorKind) return [WIZARD_STEPS.ENTITY_TYPE];

  const option = registrationOptions.find((entry) => entry.actorKind === actorKind);
  const fromApi = mapRequiredStepsToWizardSteps(option?.requiredSteps);
  if (fromApi) return fromApi;

  return (
    mapRequiredStepsToWizardSteps(getDefaultRequiredStepsForActorKind(actorKind)) || [
      WIZARD_STEPS.ENTITY_TYPE,
    ]
  );
};

export const getStepIndexInFlow = (stepItems, currentStep) =>
  Math.max(
    0,
    stepItems.findIndex((item) => item.key === currentStep),
  );

export const getChannelProfilePermissionKeysForActorKind = (actorKind) => {
  switch (actorKind) {
    case ACTOR_KINDS.DISTRIBUTOR:
      return [
        'canCreatePartners',
        'canCreateResellers',
        'canCreateCustomers',
        'canCreateReferralAgents',
      ];
    case ACTOR_KINDS.PARTNER:
    case ACTOR_KINDS.RESELLER:
      return ['canCreateCustomers', 'canCreateReferralAgents'];
    case ACTOR_KINDS.REFERRAL_AGENT:
      return [];
    default:
      return [];
  }
};

export const CHANNEL_PROFILE_PERMISSION_DEFS = Object.freeze([
  {
    key: 'canCreatePartners',
    labelId: 'global.text.canCreatePartners',
    formKey: 'channelProfile.canCreatePartners',
    summaryKey: 'summary.profileCanCreatePartners',
  },
  {
    key: 'canCreateResellers',
    labelId: 'global.text.canCreateResellers',
    formKey: 'channelProfile.canCreateResellers',
    summaryKey: 'summary.profileCanCreateResellers',
  },
  {
    key: 'canCreateCustomers',
    labelId: 'global.text.canCreateCustomers',
    formKey: 'channelProfile.canCreateCustomers',
    summaryKey: 'summary.profileCanCreateCustomers',
  },
  {
    key: 'canCreateReferralAgents',
    labelId: 'global.text.canCreateReferralAgents',
    formKey: 'channelProfile.canCreateReferralAgents',
    summaryKey: 'summary.profileCanCreateReferralAgents',
  },
]);

const CHANNEL_PROFILE_PERMISSION_DEF_BY_KEY = Object.freeze(
  Object.fromEntries(CHANNEL_PROFILE_PERMISSION_DEFS.map((def) => [def.key, def])),
);

export const getChannelProfilePermissionDef = (key) =>
  CHANNEL_PROFILE_PERMISSION_DEF_BY_KEY[key] ?? null;

export const getChannelProfilePermissionDefsForActorKind = (actorKind) =>
  getChannelProfilePermissionKeysForActorKind(actorKind)
    .map(getChannelProfilePermissionDef)
    .filter(Boolean);

if (process.env.NODE_ENV === 'development') {
  Object.values(ACTOR_KINDS).forEach((actorKind) => {
    getChannelProfilePermissionKeysForActorKind(actorKind).forEach((key) => {
      if (!CHANNEL_PROFILE_PERMISSION_DEF_BY_KEY[key]) {
        // eslint-disable-next-line no-console
        console.warn(
          `[channel-onboarding] Missing CHANNEL_PROFILE_PERMISSION_DEF for key "${key}" (actorKind: ${actorKind}).`,
        );
      }
    });
  });
}

export const getActorFieldsForType = (actorKind, wizardConfig) => {
  let fields = [];

  switch (actorKind) {
    case ACTOR_KINDS.DISTRIBUTOR:
      fields = wizardConfig.distributorFields;
      break;
    case ACTOR_KINDS.PARTNER:
      fields = wizardConfig.partnerFields;
      break;
    case ACTOR_KINDS.RESELLER:
      fields = wizardConfig.resellerFields;
      break;
    case ACTOR_KINDS.REFERRAL_AGENT:
      fields = wizardConfig.referralAgentFields;
      break;
    default:
      return [];
  }

  if (isEntityActorKind(actorKind)) {
    const profileFields = wizardConfig.getChannelProfileFields(actorKind);
    if (profileFields.length) {
      return [...fields, ...profileFields];
    }
  }

  return fields;
};

const APPROVED_AGREEMENT_APPROVAL_STATUS = getEnumValue(
  CoreEnums.ChannelAgreementApprovalStatus,
  CoreEnums.ChannelAgreementApprovalStatus.Approved,
);

const APPROVED_AGREEMENT_FILTER = `isActive eq true and approvalStatus eq ${getEnumNamespace(
  CoreEnums.ChannelAgreementApprovalStatus,
  'Channel.Agreements',
)}'${APPROVED_AGREEMENT_APPROVAL_STATUS}'`;

const AGREEMENT_TYPE_NAMESPACE = getEnumNamespace(
  CoreEnums.ChannelAgreementType,
  'Channel.Agreements',
);

const buildAgreementTypeFilter = (agreementTypeValue) => {
  const typeKey = getEnumValue(CoreEnums.ChannelAgreementType, agreementTypeValue);
  return `agreementType eq ${AGREEMENT_TYPE_NAMESPACE}'${typeKey}'`;
};

export const buildApprovedAgreementSelectParams = (agreementTypeValue) => ({
  select: 'id,name,ownerType,commissionSettlementScope',
  filter: `${APPROVED_AGREEMENT_FILTER} and ${buildAgreementTypeFilter(agreementTypeValue)}`,
  orderBy: 'name asc',
});

const enumToKebabApiValue = (enumObject, value) => {
  if (value == null || value === '') return undefined;
  const key = getEnumValue(enumObject, value);
  if (key) return _.kebabCase(key);
  if (typeof value === 'string') return value;
  return undefined;
};

const ACTIVE_ENTITY_STATUS = enumToKebabApiValue(
  CoreEnums.ChannelEntityStatus,
  CoreEnums.ChannelEntityStatus.Active,
);

const BILLING_SUBSCRIPTION_FIELD_KEYS = Object.freeze([
  'billingLegalName',
  'billingTaxId',
  'billingEmail',
  'billingAddressLine1',
]);

const pickBillingSubscription = (normalized) => {
  const billingSubscription = {};

  BILLING_SUBSCRIPTION_FIELD_KEYS.forEach((key) => {
    const formKey = `billingSubscription.${key}`;
    if (normalized[formKey]) {
      billingSubscription[key] = normalized[formKey];
    }
  });

  return Object.keys(billingSubscription).length ? billingSubscription : undefined;
};

const pickCompany = (normalized) => {
  const company = {
    name: normalized['company.name'],
    identity: normalized['company.identity'],
    countryId: normalized['company.countryId'],
  };
  if (normalized['company.commercialName']) {
    company.commercialName = normalized['company.commercialName'];
  }
  if (normalized['company.shortCode']) {
    company.shortCode = normalized['company.shortCode'];
  }
  return company;
};

const pickOwnerUser = (normalized) => {
  const ownerUser = {
    names: normalized['ownerUser.names'],
    email: normalized['ownerUser.email'],
  };
  if (normalized['ownerUser.lastNames']) {
    ownerUser.lastNames = normalized['ownerUser.lastNames'];
  }
  return ownerUser;
};

const buildChannelProfile = (normalized, actorKind) => {
  const profile = { actorKind };

  getChannelProfilePermissionKeysForActorKind(actorKind).forEach((key) => {
    const formKey = `channelProfile.${key}`;
    if (normalized[formKey] !== undefined) {
      profile[key] = !!normalized[formKey];
    }
  });

  return profile;
};

export const buildRegisterPayload = (formValues, actorKind) => {
  const normalized = normalizeOptionValues(formValues, { skipKeys: [] });
  const agreementId = normalized.agreementId || null;

  const billingSubscription = pickBillingSubscription(normalized);

  const payload = {
    automyPlanId: normalized.automyPlanId,
    ownerUser: pickOwnerUser(normalized),
    company: pickCompany(normalized),
    channelProfile: buildChannelProfile(normalized, actorKind),
  };

  if (billingSubscription) {
    payload.billingSubscription = billingSubscription;
  }

  if (actorKind === ACTOR_KINDS.DISTRIBUTOR) {
    payload.distributor = {
      agreementId,
      partnerAgreementMode: enumToKebabApiValue(
        CoreEnums.DistributorPartnerAgreementMode,
        normalized['distributor.partnerAgreementMode'],
      ),
      defaultPartnerAgreementId: normalized['distributor.defaultPartnerAgreementId'] || null,
      defaultResellerAgreementId: normalized['distributor.defaultResellerAgreementId'] || null,
      defaultReferralAgentsAgreementId:
        normalized['distributor.defaultReferralAgentsAgreementId'] || null,
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  if (actorKind === ACTOR_KINDS.PARTNER) {
    payload.partner = {
      distributorId: normalized['partner.distributorId'],
      agreementId,
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  if (actorKind === ACTOR_KINDS.RESELLER) {
    payload.reseller = {
      distributorId: normalized['reseller.distributorId'] || null,
      partnerId: normalized['reseller.partnerId'] || null,
      agreementId,
      billingResponsibility: enumToKebabApiValue(
        CoreEnums.ResellerBillingResponsibility,
        normalized['reseller.billingResponsibility'],
      ),
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  if (actorKind === ACTOR_KINDS.REFERRAL_AGENT) {
    payload.referralAgent = {
      distributorId: normalized['referralAgent.distributorId'] || null,
      agreementId,
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  return payload;
};

export const getStepFieldNames = (step, actorKind) => {
  switch (step) {
    case WIZARD_STEPS.PLAN:
      return ['automyPlanId'];
    case WIZARD_STEPS.COMPANY:
      return [
        'company.name',
        'company.identity',
        'company.countryId',
        'ownerUser.names',
        'ownerUser.email',
      ];
    case WIZARD_STEPS.ACTOR:
      if (actorKind === ACTOR_KINDS.DISTRIBUTOR) {
        return ['distributor.partnerAgreementMode'];
      }
      if (actorKind === ACTOR_KINDS.PARTNER) {
        return ['partner.distributorId'];
      }
      if (actorKind === ACTOR_KINDS.RESELLER) {
        return ['reseller.billingResponsibility'];
      }
      return [];
    default:
      return [];
  }
};

export const validateResellerParents = (values) => {
  const pickRelationId = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
      return value.value || null;
    }
    return value;
  };

  return Boolean(
    pickRelationId(values['reseller.distributorId']) ||
      pickRelationId(values['reseller.partnerId']),
  );
};

export const isWizardStepReady = ({
  currentStep,
  actorKind,
  formConfig,
  form,
  formValues = {},
  registrationOptionsLoading = false,
}) => {
  if (currentStep === WIZARD_STEPS.ENTITY_TYPE) {
    if (registrationOptionsLoading) return false;
    return Boolean(actorKind);
  }

  if (
    currentStep === WIZARD_STEPS.AGREEMENT ||
    currentStep === WIZARD_STEPS.BILLING ||
    currentStep === WIZARD_STEPS.SUMMARY
  ) {
    return true;
  }

  if (!formConfig?.fields?.length) {
    return false;
  }

  const values = { ...formValues, ...form?.getFieldsValue?.(true) };

  if (!checkFormState(formConfig, form, values)) {
    return false;
  }

  if (
    currentStep === WIZARD_STEPS.ACTOR &&
    actorKind === ACTOR_KINDS.RESELLER &&
    !validateResellerParents(values)
  ) {
    return false;
  }

  return true;
};

export const mapOnboardingValidationFieldToFormName = (fieldPath = '') => {
  if (!fieldPath) return fieldPath;

  return String(fieldPath)
    .split('.')
    .map((segment) => (segment ? _.camelCase(segment) : segment))
    .join('.');
};

export const mapOnboardingValidationErrorsForForm = (validationErrors = []) =>
  validationErrors.map((item) => ({
    ...item,
    field: mapOnboardingValidationFieldToFormName(item.field),
  }));

export const mapValidationErrorToStep = (fieldPath = '', actorKind = null) => {
  const path = String(fieldPath).toLowerCase();
  if (path.includes('automyplan')) return WIZARD_STEPS.PLAN;
  if (path.includes('owneruser')) return WIZARD_STEPS.COMPANY;
  if (path.includes('company')) return WIZARD_STEPS.COMPANY;
  if (path.includes('agreement')) return WIZARD_STEPS.AGREEMENT;
  if (path.includes('actorkind')) return WIZARD_STEPS.ENTITY_TYPE;
  if (path.includes('channelprofile')) {
    return isEntityActorKind(actorKind) ? WIZARD_STEPS.ACTOR : WIZARD_STEPS.COMPANY;
  }
  if (path.includes('billingsubscription')) return WIZARD_STEPS.BILLING;
  if (
    path.includes('distributor') ||
    path.includes('partner') ||
    path.includes('reseller') ||
    path.includes('referralagent')
  ) {
    return WIZARD_STEPS.ACTOR;
  }
  return WIZARD_STEPS.SUMMARY;
};
